/**
 * Was eine Package-Datenquelle sich merkt, und was der Fragebogen der
 * Package-Vorlagen mit dem Server austauscht (5.1 Abschnitt 3.37, R892–R897).
 *
 * **Dieselbe Form wie im Dienst** (`Packagezuschnitt`): Der Fragebogen schickt
 * sie, die Datenquelle speichert sie, der Import prüft gegen sie.
 */

export type Ebene = 'position' | 'konzern' | 'lokal'

export type Spiegelart = 'anlagen' | 'verbindlichkeiten' | 'rueckstellungen' | 'eigenkapital'

export type Zusatzspalte = 'segment' | 'funktionsbereich' | 'kostenstelle'

export const SPIEGELARTEN: readonly Spiegelart[] = ['anlagen', 'verbindlichkeiten', 'rueckstellungen', 'eigenkapital']

export const ZUSATZSPALTEN: readonly Zusatzspalte[] = ['segment', 'funktionsbereich', 'kostenstelle']

export type Packagezuschnitt = {
  ebene: Ebene
  partner: boolean
  spiegel: Spiegelart[]
  eroeffnung: boolean
  dimensionen: Zusatzspalte[]
  faktor: 1 | 1000
  /** Je Position (Schlüssel) ihr Stellvertreterkonto (Schlüssel) — R894. */
  stellvertreter: Record<string, string>
}

export type Packagevorlagenauftrag = {
  quelle: string | null
  neueQuelle: string | null
  gesellschaften: string[]
  periode: number
  zuschnitt: Packagezuschnitt
}

export type Vorlagenquelle = { schluessel: string; name: string; zuschnitt: Packagezuschnitt | null }

export type Vorlagengesellschaft = { schluessel: string; name: string; erstesPackage: boolean }

export type Packagevorlagengrundlagen = {
  quellen: Vorlagenquelle[]
  gesellschaften: Vorlagengesellschaft[]
  kostenstelleGefuehrt: boolean
}

export type Stellvertreterkonto = { schluessel: string; nummer: string; name: string }

export type Stellvertreterfrage = {
  position: string
  code: string
  name: string
  konten: Stellvertreterkonto[]
  gewaehlt: string | null
}

export type Vorlagenumfang = {
  gesellschaft: string
  name: string
  dateiname: string
  blaetter: { blatt: string; zeilen: number }[]
  hinweis: string | null
}

export type Packagevorlagenvorschau = {
  mappen: Vorlagenumfang[]
  stellvertreter: Stellvertreterfrage[]
  befunde: string[]
}

/** Die Antworten ab Werk — jede vorbelegt, damit „Erzeugen" sofort ein Package ergibt. */
export const ZUSCHNITT_AB_WERK: Packagezuschnitt = {
  ebene: 'lokal',
  partner: true,
  spiegel: [],
  eroeffnung: false,
  dimensionen: [],
  faktor: 1,
  stellvertreter: {},
}

/**
 * Ein gespeicherter Zuschnitt, so gelesen, dass jedes Feld dasteht — der
 * Dienst lässt leere Listen auch einmal weg.
 */
export function zuschnittLesen(roh: Partial<Packagezuschnitt> | null | undefined): Packagezuschnitt {
  return {
    ebene: roh?.ebene === 'position' || roh?.ebene === 'konzern' ? roh.ebene : 'lokal',
    partner: roh?.partner ?? true,
    spiegel: (roh?.spiegel ?? []).filter((s): s is Spiegelart => SPIEGELARTEN.includes(s)),
    eroeffnung: roh?.eroeffnung ?? false,
    dimensionen: (roh?.dimensionen ?? []).filter((d): d is Zusatzspalte => ZUSATZSPALTEN.includes(d)),
    faktor: roh?.faktor === 1000 ? 1000 : 1,
    stellvertreter: { ...(roh?.stellvertreter ?? {}) },
  }
}

/** Ein Wert in einer Liste an- oder abgewählt — die Reihenfolge des Vorrats bleibt. */
export function umgeschaltet<T>(liste: readonly T[], wert: T, an: boolean, vorrat: readonly T[]): T[] {
  const neu = new Set(liste)
  if (an) neu.add(wert)
  else neu.delete(wert)
  return vorrat.filter((v) => neu.has(v))
}

/** Aus 202612 wird „2026-12" für das Monatsfeld, und zurück. */
export function alsMonat(periode: number): string {
  return `${Math.floor(periode / 100)}-${String(periode % 100).padStart(2, '0')}`
}

export function ausMonat(text: string): number {
  const [jahr, monat] = text.split('-').map(Number)
  return jahr && monat ? jahr * 100 + monat : 0
}
